import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  AdminLinkAction,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import defaultUserAvatar from '../../../assets/img/User.png';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  ARCHIVE_FINAL_OUTCOME_OPTIONS,
  ARCHIVE_FINALISED_DATE_OPTIONS,
  FRAUD_ARCHIVE_ROUTE,
  archiveListRow,
  collectArchiveCases,
  formatFraudArchiveDateTime,
  getArchiveTypeFilterOptions,
  matchesArchiveFilters,
  matchesArchiveSearch,
} from '../../../data/fraudArchiveSpec';

const DEFAULT_FILTERS = {
  type: 'All',
  finalOutcome: 'All',
  dateRange: 'All',
};

/** Case ID · Account · Source · Type · Final Outcome · Decision ID · Case Reason · Priority · Finalised On · Actions (old order + Decision ID / Case Reason / Priority) */
const TABLE_GRID =
  'minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 0.8fr) minmax(0, 0.95fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(0, 1.25fr) minmax(0, 0.65fr) minmax(0, 1fr) minmax(62px, auto)';

const priorityTextClass = (priority) => {
  if (priority === 'High') return 'text-alertred';
  if (priority === 'Medium') return 'text-warmyellow';
  return '';
};

const FraudSecurityArchive = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [items] = useState(() => collectArchiveCases());
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [typeOpen, setTypeOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [rowActionsOpen, setRowActionsOpen] = useState({});

  const typeRef = useRef(null);
  const outcomeRef = useRef(null);
  const dateRef = useRef(null);
  const rowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: rowActionsMenuLayout,
    menuRef: rowActionsMenuRef,
    placementReady: rowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.SINGLE
  );

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(typeRef, () => setTypeOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));
  useClickOutside(dateRef, () => setDateOpen(false));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch]);

  const filteredItems = useMemo(
    () =>
      items.filter(
        (row) => matchesArchiveSearch(row, debouncedSearch) && matchesArchiveFilters(row, filters)
      ),
    [items, filters, debouncedSearch]
  );

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(filteredItems.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [filteredItems.length]);

  const paginatedItems = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return filteredItems.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [filteredItems, effectivePage]);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const openCase = (item, e) => {
    if (e) e.stopPropagation();
    navigate(`${FRAUD_ARCHIVE_ROUTE}/${item.caseId}`, {
      state: { from: location.pathname, item },
    });
  };

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Fraud & Security Archive</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Case ID, Decision ID, Account, Flag ID…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search archived Fraud & Security cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Finalised Date"
                value={filters.dateRange}
                options={ARCHIVE_FINALISED_DATE_OPTIONS}
                onChange={(value) => setFilters((prev) => ({ ...prev, dateRange: value }))}
                menuRef={dateRef}
                open={dateOpen}
                setOpen={setDateOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Final Outcome"
                value={filters.finalOutcome}
                options={ARCHIVE_FINAL_OUTCOME_OPTIONS}
                onChange={(value) => setFilters((prev) => ({ ...prev, finalOutcome: value }))}
                menuRef={outcomeRef}
                open={outcomeOpen}
                setOpen={setOutcomeOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Type"
                value={filters.type}
                options={getArchiveTypeFilterOptions()}
                onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
                menuRef={typeRef}
                open={typeOpen}
                setOpen={setTypeOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div className="min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{ gridTemplateColumns: TABLE_GRID }}
              >
                {[
                  'Case ID',
                  'Account',
                  'Source',
                  'Type',
                  'Final Outcome',
                  'Decision ID',
                  'Case Reason',
                  'Priority',
                  'Finalised On',
                ].map((label) => (
                  <div key={label} className="flex items-center min-w-0">
                    <AdminTableHeader>{label}</AdminTableHeader>
                  </div>
                ))}
                <div className="flex items-center justify-center min-w-0">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {paginatedItems.length === 0 ? (
                <div className="py-10 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No archived cases match your search or filters.
                  </AdminSupportingText>
                </div>
              ) : (
                paginatedItems.map((row) => {
                  const item = archiveListRow(row);
                  return (
                    <div
                      key={item.caseId}
                      role="button"
                      tabIndex={0}
                      onClick={() => openCase(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openCase(item);
                        }
                      }}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                      style={{ gridTemplateColumns: TABLE_GRID }}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminLinkAction type="button" onClick={(e) => openCase(item, e)}>
                          {item.caseId}
                        </AdminLinkAction>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <AdminRowAvatar
                          src={item.accountAvatar || defaultUserAvatar}
                          alt={item.accountName || ''}
                          onError={(e) => {
                            e.target.src = defaultUserAvatar;
                          }}
                        />
                        <AdminTableBody className="truncate" title={item.accountName}>
                          {item.accountName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate">{item.source}</AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.caseType || '—'}>
                          {item.caseType || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge status={item.finalOutcome} label={item.finalOutcome} />
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.decisionId}>
                          {item.decisionId}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.caseReason || '—'}>
                          {item.caseReason || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className={priorityTextClass(item.priority)}>
                          {item.priority || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta className="whitespace-nowrap">
                          {formatFraudArchiveDateTime(item.finalisedOn)}
                        </AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[item.caseId] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(item.caseId);
                            }}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[item.caseId] && rowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRowActionsOpen({});
                                  openCase(item, e);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default FraudSecurityArchive;
